'use client';

import { createLocalStorageStore, type LocalStorageStore } from '@fpo3/ts-commons-ui';
import type { PillPosition } from './adapters';

/**
 * Per-app spacing hints for the docked pill. The jobs are named distinctly so
 * the type can't be misread:
 *
 *   - `edgeInset` pushes the pill further in from whichever edge it docks to
 *     (primary axis, all four edges). Adds to the base `EDGE_INSET`. Use this
 *     to clear fixed chrome on the docked edge (e.g. a top nav when top-docked).
 *   - `topClearance` / `bottomClearance` are **perpendicular** clamp bounds for
 *     **left/right** docking only — they tighten the vertical range, keeping the
 *     pill clear of an app's fixed top nav / bottom bar. They do not affect the
 *     primary inset when the pill is top- or bottom-docked.
 *   - `leftClearance` / `rightClearance` are **perpendicular** clamp bounds for
 *     **top/bottom** docking only — they tighten the horizontal range, keeping
 *     the pill clear of side chrome.
 */
export interface PillOffset {
  edgeInset?: number | undefined;
  topClearance?: number | undefined;
  bottomClearance?: number | undefined;
  leftClearance?: number | undefined;
  rightClearance?: number | undefined;
}

/**
 * Internal rich position: which viewport edge the pill is docked to, plus
 * the px offset along that edge. For left/right edges the perpendicular
 * axis is vertical (`perpendicularOffset` = `top`); for top/bottom edges
 * the perpendicular axis is horizontal (`perpendicularOffset` = `left`).
 *
 * Note: `'hidden'` is intentionally NOT part of this type. The legacy
 * `PillPosition` prop's `'hidden'` value is handled at the component
 * boundary (`DevPillImpl` short-circuits before rendering `PillUI`), so
 * `EdgePosition`-consuming helpers in this module never see the sentinel.
 */
export interface EdgePosition {
  edge: 'left' | 'right' | 'top' | 'bottom';
  perpendicularOffset: number;
}

type Edge = 'left' | 'right' | 'top' | 'bottom';

/** Inset (px) from the docked edge — keeps the pill clear of the viewport corner. */
export const EDGE_INSET = 8;
/**
 * Margin (px) from the perpendicular bounds on **coarse (touch) pointers** —
 * keeps the pill clear of viewport corners. Sized
 * to clear typical mobile OS chrome that intercepts touches in a corner even
 * when nothing visible covers the pill: iOS status bar / Dynamic Island
 * (~44–54px), the home-indicator swipe zone (~34px), and Android system bars.
 * (FPO-NA: the pill could previously be docked 8px into a corner and become
 * un-tappable on touch.) Per-app fixed chrome taller than this is reserved
 * additionally via the consumer `offset` (see {@link clampPerpendicularOffset}
 * `topInset`/`bottomInset`).
 *
 * On **fine (mouse) pointers** there is no OS chrome stealing corner clicks, so
 * the caller passes {@link FINE_POINTER_PERPENDICULAR_MARGIN} instead — letting
 * a desktop pill sit in a true corner. The pure helpers in this module take the
 * effective margin as a parameter (defaulting to this constant); the
 * coarse-vs-fine decision is made once in `useDraggablePill` via
 * `matchMedia('(pointer: coarse)')`.
 */
export const PERPENDICULAR_MARGIN = 60;
/**
 * Perpendicular margin (px) on fine (mouse) pointers — equal to
 * {@link EDGE_INSET} so the keep-out matches the edge inset and the pill
 * docks into a symmetric true corner on desktop.
 */
export const FINE_POINTER_PERPENDICULAR_MARGIN = 8;
/** Fallback pill width (px) used during the first paint before the container has been measured. */
export const FALLBACK_PILL_WIDTH = 200;
/** Fallback pill height (px) used during the first paint before the container has been measured. */
export const FALLBACK_PILL_HEIGHT = 30;
/** Fallback viewport size (px) used to seed the initial perpendicular offset before layout has run. */
export const FALLBACK_VIEWPORT_WIDTH = 1280;
export const FALLBACK_VIEWPORT_HEIGHT = 800;
/** Next.js dev indicator offset (px). Applied when a right-docked pill is near a top/bottom corner. */
const NEXTJS_INDICATOR_OFFSET = 64;
/** Size of the right-edge corner zone where the Next.js indicator interferes (px). */
const NEXTJS_NEAR_RIGHT_ZONE = 80;

interface ViewportSize {
  width: number;
  height: number;
}

interface PillSize {
  width: number;
  height: number;
}

interface PillRect extends PillSize {
  x: number;
  y: number;
}

/**
 * Clamp `perpendicularOffset` so the pill stays on-screen with a
 * {@link PERPENDICULAR_MARGIN} gap from each perpendicular bound. Caller
 * passes the relevant viewport dimension (height for left/right edges,
 * width for top/bottom edges).
 *
 * `topInset` / `bottomInset` reserve *additional* clearance beyond the base
 * margin at the near / far bound respectively — this is how a consumer's
 * clearance offsets keep the pill clear of that app's fixed chrome.
 *
 * `perpendicularMargin` overrides the base keep-out band (default
 * {@link PERPENDICULAR_MARGIN}). `useDraggablePill` passes
 * {@link FINE_POINTER_PERPENDICULAR_MARGIN} on mouse pointers so a desktop pill
 * can reach a true corner.
 */
export function clampPerpendicularOffset(
  offset: number,
  pillSize: number,
  viewportSize: number,
  insets?: { topInset?: number; bottomInset?: number; perpendicularMargin?: number | undefined }
): number {
  const margin = insets?.perpendicularMargin ?? PERPENDICULAR_MARGIN;
  const min = margin + (insets?.topInset ?? 0);
  const max = Math.max(min, viewportSize - pillSize - margin - (insets?.bottomInset ?? 0));
  if (offset < min) return min;
  if (offset > max) return max;
  return offset;
}

/**
 * Returns the (signed) px to add along the pill's PERPENDICULAR axis so the
 * pill clears the Next.js dev indicator. The indicator lives at the top-right
 * or bottom-right corner:
 *
 *   - right edge: perpendicular is vertical. Near the top → push down +64px;
 *     near the bottom → push up -64px.
 *   - top/bottom edge: perpendicular is horizontal. Near the right → push
 *     left -64px.
 *   - left edge: no interference.
 */
function computeIndicatorNudge(
  edge: Edge,
  clampedPerp: number,
  viewport: ViewportSize,
  pillSize: PillSize,
  nextjsVisible: boolean
): number {
  if (!nextjsVisible) return 0;
  if (edge === 'right') {
    const nearTop = clampedPerp <= NEXTJS_NEAR_RIGHT_ZONE;
    const nearBottom = viewport.height - clampedPerp - pillSize.height <= NEXTJS_NEAR_RIGHT_ZONE;
    if (nearTop) return NEXTJS_INDICATOR_OFFSET;
    if (nearBottom) return -NEXTJS_INDICATOR_OFFSET;
  }
  if (edge === 'top' || edge === 'bottom') {
    const nearRight = viewport.width - clampedPerp - pillSize.width <= NEXTJS_NEAR_RIGHT_ZONE;
    if (nearRight) return -NEXTJS_INDICATOR_OFFSET;
  }
  return 0;
}

/**
 * Compute the host-element style for the DevPill based on its docked edge,
 * perpendicular offset along that edge, and whether the Next.js dev indicator
 * is currently visible. Applied to the shadow-DOM host via
 * `Object.assign(host.style, computePillHostStyle(...))`.
 *
 * `EDGE_INSET` (8px) is the gap between the pill and the docked edge. The
 * unused axes are always cleared with `''` so a previous edge does not leave
 * a stale rule on the host element.
 */
export function computePillHostStyle(
  position: EdgePosition,
  nextjsVisible: boolean,
  viewport: ViewportSize,
  pillSize: PillSize,
  offset?: PillOffset | undefined,
  perpendicularMargin?: number
): { top: string; bottom: string; left: string; right: string } {
  const empty = { top: '', bottom: '', left: '', right: '' };
  const { edge, perpendicularOffset } = position;

  if (edge === 'left' || edge === 'right') {
    // Perpendicular axis is vertical (height + top).
    const clamped = clampPerpendicularOffset(
      perpendicularOffset,
      pillSize.height,
      viewport.height,
      {
        topInset: offset?.topClearance ?? 0,
        bottomInset: offset?.bottomClearance ?? 0,
        perpendicularMargin,
      }
    );
    const perpNudge = computeIndicatorNudge(edge, clamped, viewport, pillSize, nextjsVisible);
    return {
      ...empty,
      [edge]: `${EDGE_INSET + (offset?.edgeInset ?? 0)}px`,
      top: `${clamped + perpNudge}px`,
    };
  }

  // top/bottom: perpendicular axis is horizontal (width + left).
  const clamped = clampPerpendicularOffset(perpendicularOffset, pillSize.width, viewport.width, {
    topInset: offset?.leftClearance ?? 0,
    bottomInset: offset?.rightClearance ?? 0,
    perpendicularMargin,
  });
  const perpNudge = computeIndicatorNudge(edge, clamped, viewport, pillSize, nextjsVisible);
  return {
    ...empty,
    [edge]: `${EDGE_INSET + (offset?.edgeInset ?? 0)}px`,
    left: `${clamped + perpNudge}px`,
  };
}

/**
 * Snap a free-floating pill rect to the nearest viewport edge based on the
 * pill center's distance to each of the four edges. Returns the snapped
 * `EdgePosition` with the perpendicular offset clamped to the in-screen
 * range (vertical for left/right; horizontal for top/bottom).
 *
 * Tie-break order: left, top, right, bottom — the first candidate with the
 * minimum distance wins.
 */
export function snapToNearestEdge(
  pill: PillRect,
  viewport: ViewportSize,
  perpendicularMargin?: number
): EdgePosition {
  const centerX = pill.x + pill.width / 2;
  const centerY = pill.y + pill.height / 2;
  const distances: ReadonlyArray<{ edge: Edge; dist: number }> = [
    { edge: 'left', dist: centerX },
    { edge: 'top', dist: centerY },
    { edge: 'right', dist: viewport.width - centerX },
    { edge: 'bottom', dist: viewport.height - centerY },
  ];
  let nearest = distances[0]!;
  for (let i = 1; i < distances.length; i++) {
    if (distances[i]!.dist < nearest.dist) nearest = distances[i]!;
  }
  const edge = nearest.edge;
  if (edge === 'left' || edge === 'right') {
    return {
      edge,
      perpendicularOffset: clampPerpendicularOffset(pill.y, pill.height, viewport.height, {
        perpendicularMargin,
      }),
    };
  }
  return {
    edge,
    perpendicularOffset: clampPerpendicularOffset(pill.x, pill.width, viewport.width, {
      perpendicularMargin,
    }),
  };
}

/**
 * Direction modifiers for popups anchored to the pill — a small product of
 * (vertical: open downward) and (horizontal: anchor left so popup extends
 * to the right). Driven by both the docked edge AND the pill's center along
 * the perpendicular axis so popups always open into the viewport interior,
 * never off-screen at a corner.
 */
export interface PopupDirection {
  /** Open downward (default is upward — popup `bottom: 100%`). */
  down: boolean;
  /** Anchor to the left of the trigger so popup extends rightward (default is right-anchor → leftward extension). */
  rightAnchor: boolean;
}

/**
 * Pure derivation of popup direction from the pill's docked edge plus its
 * center along the perpendicular axis.
 *
 * Left/right edges: `down` is driven by vertical center (upper half → down);
 * `rightAnchor` by the edge (left → right-anchor).
 *
 * Top/bottom edges: `down` is forced by the edge (top → down, bottom → up);
 * `rightAnchor` is driven by horizontal center (left half → right-anchor).
 */
export function popupDirectionFor(
  position: EdgePosition,
  viewport: ViewportSize,
  pillSize: PillSize,
  offset?: PillOffset | undefined,
  perpendicularMargin?: number
): PopupDirection {
  const { edge, perpendicularOffset } = position;

  if (edge === 'left' || edge === 'right') {
    const perpSize = pillSize.height;
    const clamped = clampPerpendicularOffset(perpendicularOffset, perpSize, viewport.height, {
      topInset: offset?.topClearance ?? 0,
      bottomInset: offset?.bottomClearance ?? 0,
      perpendicularMargin,
    });
    const inUpperHalf = clamped + perpSize / 2 < viewport.height / 2;
    return { down: inUpperHalf, rightAnchor: edge === 'left' };
  }

  // top/bottom: perpendicular axis is horizontal.
  const perpSize = pillSize.width;
  const clamped = clampPerpendicularOffset(perpendicularOffset, perpSize, viewport.width, {
    topInset: offset?.leftClearance ?? 0,
    bottomInset: offset?.rightClearance ?? 0,
    perpendicularMargin,
  });
  const inLeftHalf = clamped + perpSize / 2 < viewport.width / 2;
  return { down: edge === 'top', rightAnchor: inLeftHalf };
}

/**
 * Seed the internal `EdgePosition` from the legacy `PillPosition` prop. Used
 * as a fallback when nothing is in localStorage. Both legacy corners map to
 * the **right** edge; the vertical hint is preserved: `top-right` → right
 * edge near the top; `bottom-right` → right edge near the bottom.
 */
export function edgePositionFromPillPosition(
  position: Exclude<PillPosition, 'hidden'>,
  viewport: ViewportSize,
  pillSize: PillSize,
  perpendicularMargin: number = PERPENDICULAR_MARGIN
): EdgePosition {
  // The perpendicular axis is vertical for left/right edges. `top-right`
  // seeds near the top (lower bound = perpendicularMargin); `bottom-right`
  // seeds near the bottom (max in-bounds offset). Computed explicitly rather
  // than via `clampPerpendicularOffset(Infinity, …)` so the clamp internals
  // don't leak here; `Math.max(perpendicularMargin, …)` mirrors the clamp's
  // lower-bound behavior when the pill is taller than the viewport.
  const perpendicularOffset =
    position === 'top-right'
      ? perpendicularMargin
      : Math.max(perpendicularMargin, viewport.height - pillSize.height - perpendicularMargin);
  return { edge: 'right', perpendicularOffset };
}

/** Type guard that validates a deserialized localStorage value is a valid EdgePosition. */
function isPersistedEdgePosition(value: unknown): value is EdgePosition {
  if (typeof value !== 'object' || value == null) return false;
  const record = value as Record<string, unknown>;
  const edge = record['edge'];
  const perpendicularOffset = record['perpendicularOffset'];
  return (
    (edge === 'left' || edge === 'right' || edge === 'top' || edge === 'bottom') &&
    typeof perpendicularOffset === 'number' &&
    Number.isFinite(perpendicularOffset)
  );
}

/**
 * Cross-app DevPill position persistence backed by the standard
 * `createLocalStorageStore` helper. Centralizes the try/catch + custom-event
 * dance the hand-rolled load/save helpers used to duplicate.
 *
 * The `devpill:` key prefix is intentionally a cross-service namespace (the
 * pill is rendered by famfam, wt-web, and agent-history through `dev-proxy`)
 * — the user's chosen position should follow them across apps.
 */
export const pillPositionStore: LocalStorageStore<EdgePosition | null> =
  createLocalStorageStore<EdgePosition | null>({
    key: 'devpill:position:v1',
    eventName: 'devpill:position:change',
    parse: (raw) => {
      try {
        const parsed: unknown = JSON.parse(raw);
        return isPersistedEdgePosition(parsed) ? parsed : undefined;
      } catch {
        return undefined;
      }
    },
    serialize: (value) => JSON.stringify(value),
    defaultValue: null,
  });

/**
 * Identifies which app is rendering the DevPill. Unlike the dragged position
 * (cross-app), the collapsed/expanded choice is persisted **per app** — a user
 * can keep the pill expanded on wt-web (constant worktree switching) while
 * collapsed on famfam.
 */
export type DevPillAppId = 'famfam' | 'wt-web' | 'agent-history' | 'bridge';

const expandedStores = new Map<DevPillAppId, LocalStorageStore<boolean>>();

/**
 * Per-app store for the DevPill's expanded/collapsed state. Defaults to
 * **collapsed** (`false`) on first load. Memoized per `appId` (the key is
 * dynamic, so this is not a single module singleton like
 * {@link pillPositionStore}); cross-tab sync is scoped to the same app via the
 * per-app event name.
 */
export function getExpandedStore(appId: DevPillAppId): LocalStorageStore<boolean> {
  const existing = expandedStores.get(appId);
  if (existing != null) return existing;
  const store = createLocalStorageStore<boolean>({
    key: `devpill:expanded:${appId}:v1`,
    eventName: `devpill:expanded:${appId}:change`,
    parse: (raw) => {
      const parsed: unknown = JSON.parse(raw);
      return typeof parsed === 'boolean' ? parsed : undefined;
    },
    serialize: (value) => JSON.stringify(value),
    defaultValue: false,
  });
  expandedStores.set(appId, store);
  return store;
}
